import type { IRowNode } from 'ag-grid-enterprise'
import type { IAuthorization } from '~/types/authorization'
import type { IRole } from '~/types/role'
import type { IUserAdmin, IUserPublic } from '~/types/user'
import type { IAppVersionWithChecklist } from '~/types/version'

import { dateComparator } from './date'

export const valueComparator = (a: string, b: string): number => {
  if (!a.length) return 1
  if (!b.length) return -1
  return a.toLowerCase().localeCompare(b.toLowerCase())
}

export const numberComparator = (a: number, b: number): number => {
  return a > b ? 1 : a < b ? -1 : 0
}

export const authorizationsComparator = (
  _: unknown,
  __: unknown,
  { data: firstItem }: IRowNode<IAuthorization>,
  { data: secondItem }: IRowNode<IAuthorization>
): number => {
  if (firstItem && secondItem) {
    return firstItem.key.localeCompare(secondItem.key)
  }
  return firstItem ? -1 : 1
}

export const rolesComparator = (
  _: unknown,
  __: unknown,
  { data: firstItem }: IRowNode<IRole>,
  { data: secondItem }: IRowNode<IRole>
): number => {
  if (firstItem && secondItem) {
    return firstItem.name.localeCompare(secondItem.name)
  }
  return firstItem ? -1 : 1
}

export const userFieldsComparator = (firstUser?: IUserPublic, secondUser?: IUserPublic): number => {
  if (!firstUser) return 1
  if (!secondUser) return -1

  const { name: firstName } = firstUser
  const { name: secondName } = secondUser

  if (!firstName) return 1
  if (!secondName) return -1
  return firstName.localeCompare(secondName)
}

export const usersComparator = (
  _: unknown,
  __: unknown,
  { data: userA }: IRowNode<IUserAdmin | IUserPublic>,
  { data: userB }: IRowNode<IUserAdmin | IUserPublic>
): number => {
  if (!userA?.name) return 1
  if (!userB?.name) return -1
  return userA.name.localeCompare(userB.name)
}

export const lastUpdateComparator = <T extends IDBContext>(
  _: unknown,
  __: unknown,
  { data: firstItem }: IRowNode<T>,
  { data: secondItem }: IRowNode<T>
): number => {
  if (firstItem && secondItem) {
    return dateComparator(firstItem.updated_at_ts, secondItem.updated_at_ts)
  }
  return firstItem ? -1 : 1
}

export const lastUpdateComparatorV2 = <T extends IDBContextV2>(
  _: unknown,
  __: unknown,
  { data: firstItem }: IRowNode<T>,
  { data: secondItem }: IRowNode<T>
): number => {
  if (firstItem && secondItem) {
    return dateComparator(firstItem.updated_at, secondItem.updated_at)
  }
  return firstItem ? -1 : 1
}

export const versionSemanticComparator = (
  _: unknown,
  __: unknown,
  { data: firstItem }: IRowNode<IAppVersionWithChecklist>,
  { data: secondItem }: IRowNode<IAppVersionWithChecklist>
): number => {
  if (firstItem && secondItem) {
    return firstItem.semantic_version.localeCompare(secondItem.semantic_version, undefined, {
      numeric: true,
      sensitivity: 'base',
    })
  }
  return firstItem ? -1 : 1
}

/**
 * It takes two semantic version numbers (strings) and orders them from oldest to newest.
 * Pass this util as callback to an Array.reduce() method.
 */
export const semverComparator = (a: string, b: string): number => {
  const [majorA, minorA, patchA] = a.split('.').map(Number)
  const [majorB, minorB, patchB] = b.split('.').map(Number)

  if (majorA > majorB) return 1
  if (majorA < majorB) return -1
  if (minorA > minorB) return 1
  if (minorA < minorB) return -1
  if (patchA > patchB) return 1
  if (patchA < patchB) return -1

  return 0
}

export const scoreComparator = (
  valueA: {
    type: string
    value: number
  },
  valueB: {
    type: string
    value: number
  }
): number => {
  if (!valueA) return 1
  if (!valueB) return -1

  const scoreA = valueA.value
  const scoreB = valueB.value

  return scoreA > scoreB ? 1 : scoreA < scoreB ? -1 : 0
}

export const textComparator = (_: string | null, gridValue: string, filterText: string | null): boolean => {
  if (filterText) {
    const filterTextLowerCase = filterText.toLowerCase()
    const valueLowerCase = gridValue.toLowerCase()

    return valueLowerCase.indexOf(filterTextLowerCase) >= 0
  }

  return false
}

export const disabledComparator = (): number => 0
